import React from 'react';
import cn from 'classnames';
import videojs from 'video.js';

import { Muted, Pause, Play, UnMuted } from './Icons';
import css from './VideoJSPlayer.module.scss';

interface Props {
  id: string;
  videoSRC: string;
  cover?: string;
  startFrom?: number; // in seconds
  getPosition?: (value: { pauseTime: number; persents: number }) => void;
  onStatisticUpdate?: (value: { fileName: string; progress: number; duration: string }) => void;
  onComplete?: () => void;
  onStartPlaying?: (videoSrc: string) => void;
  onReady?: () => void;
  bindFullscreenClick?: () => void;
  smallButtons?: boolean;
  isRepeated?: boolean;
  isDefaultControls?: boolean;
  isCenteredPlayBtn?: boolean;
  onExternalPause?: boolean;
  playButtonClassName?: string;
  className: string;
  isActive?: boolean;
}

export const VideoJS = (props: Props) => {
  const {
    onStartPlaying,
    videoSRC,
    onComplete,
    cover,
    id,
    startFrom,
    smallButtons = false,
    isRepeated = false,
    isDefaultControls = false,
    onReady,
    bindFullscreenClick,
    onExternalPause = false,
    className = '',
    isActive,
  } = props;
  const playerRef = React.useRef<ReturnType<typeof videojs> | null>(null);
  const [isReady, setIsReady] = React.useState(false);
  const [isMuted, setIsMuted] = React.useState(true);
  const [isPlaying, setIsPlaying] = React.useState(false);
  const [isDefaultUIControls, setIsDefaultUIControls] = React.useState(isDefaultControls);

  React.useEffect(() => {
    const options = {
      autoplay: isActive,
      playsinline: true,
      muted: true, //window.isUserAction === undefined ? true : window.isUserAction,
      controls: true,
      responsive: true,
      fluid: true,
      experimentalSvgIcons: true,
      loop: isRepeated,
      sources: [{ src: videoSRC, type: 'video/mp4' }],
    };

    if (!playerRef.current) {
      const videoElement = document.createElement('video-js');
      videoElement.setAttribute('id', id);
      videoElement.classList.add('vjs-big-play-centered');

      const videoElem = document.getElementById(id);
      videoElem?.appendChild(videoElement);

      const playerInstance = videojs(videoElement, options, function () {
        // this.playsinline(true);
        handlePlayerEvents(this);
      });

      playerRef.current = playerInstance ?? null;
    } else {
      const player = playerRef.current;

      player.autoplay(options.autoplay);
      player.src(options.sources);
    }
  }, [videoSRC, isActive]);

  // Dispose the Video.js player when the functional component unmounts
  React.useEffect(() => {
    const player = playerRef.current;

    return () => {
      if (player && !player.isDisposed()) {
        player.dispose();
        playerRef.current = null;
      }
    };
  }, [playerRef]);

  React.useEffect(() => {
    const player = playerRef.current;

    if (!player) return;

    if ((isPlaying && !isActive) || onExternalPause) {
      player.pause();
      setIsPlaying(false);
    } else if (isReady && isActive && !onExternalPause) {
      const playPromise = player.play();

      // prevent DevTools error
      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            if (!player.isDisposed()) player.play();
            const mutedState = window.isUserAction === undefined ? true : !window.isUserAction;
            player.muted(mutedState);
            setIsMuted(mutedState);
          })
          .catch((error: string) => {
            console.info(error);
          });
      }
    }
  }, [isReady, isActive, onExternalPause]);

  const handlePlayerEvents = (player: ReturnType<typeof videojs>) => {
    playerRef.current = player;

    setIsReady(true);

    if (startFrom && !isPlaying) {
      player.currentTime(startFrom);
    }

    // You can handle player events here, for example:
    player.on('waiting', () => {
      setIsPlaying(true);
    });

    player.on('playing', () => {
      setIsPlaying(true);
    });

    player.on('play', () => {
      onStartPlaying?.(videoSRC);
    });

    player.on('pause', () => {
      setIsPlaying(false);
    });

    player.on('fullscreenchange', () => {
      if (!isDefaultControls) {
        onFullScreenChange();
      }
    });

    player.on('ended', () => {
      if (onComplete) {
        onComplete();
        setIsPlaying(false);
      }
    });

    if (onReady) {
      onReady();
    }
  };

  const handleMute = () => {
    const player = playerRef.current;

    if (player) {
      const isMuted = player.muted();

      player.muted(!isMuted);
      setIsMuted(!isMuted);

      /** UX: Global mute flag */
      window.isUserAction = isMuted;
    }
  };

  const onFullScreenChange = () => {
    // this function handle UI controls
    const player = playerRef.current;

    if (!player) return;

    const isFullScreen = player.isFullscreen();

    setIsDefaultUIControls(Boolean(isFullScreen));
  };

  const handleFullScreen = () => {
    if (bindFullscreenClick) {
      bindFullscreenClick();
      return;
    }

    const player = playerRef.current;

    if (!player) return;

    if (player.requestFullscreen) {
      player.requestFullscreen();
    }
  };

  const onPlayClick = () => {
    const player = playerRef.current;

    if (player) {
      if (isPlaying) {
        player.pause();
      } else {
        player.play();
      }
    }
  };

  return (
    <div data-vjs-player className={cn(css.player, className, isDefaultUIControls && css.isCustomControls)} id={id}>
      {cover && (
        <div className={css.cover} onClick={onPlayClick} style={cover ? { backgroundImage: `url(${cover})` } : {}} />
      )}
      <div className={css.castomControls}>
        <button type="button" className={cn(css.controlButton, smallButtons && css.small)} onClick={handleMute}>
          {isMuted ? <Muted /> : <UnMuted />}
        </button>
        <button type="button" className={cn(css.controlButton, smallButtons && css.small)} onClick={onPlayClick}>
          {isPlaying ? <Pause /> : <Play />}
        </button>
      </div>
    </div>
  );
};

export default VideoJS;
